import React, { createContext, useContext, useEffect } from 'react';
import { useFeatures } from '../hooks/useFeatures';
import { useAuthContext } from './AuthContext';
import { FEATURE_FLAGS, type FeatureName } from '../utils/features';
import { storage } from '../utils/storage';
import { STORAGE_KEYS, createFeatureKey } from '../constants/storageKeys';

interface FeaturesContextType {
  enabledFeatures: Record<FeatureName, boolean>;
  toggleFeature: (feature: FeatureName) => Promise<void>;
  isLoading: boolean;
}

const FeaturesContext = createContext<FeaturesContextType | null>(null);

export function useFeaturesContext() {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error('useFeaturesContext must be used within a FeaturesProvider');
  }
  return context;
}

interface FeaturesProviderProps {
  children: React.ReactNode;
}

class FeatureErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error('Features error:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Error Loading Features
            </h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Please refresh the page to try again
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function LoadingSpinner() {
  return (
    <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
    </div>
  );
}

export function FeaturesProvider({ children }: FeaturesProviderProps) {
  const { session, isLoading: authLoading } = useAuthContext();
  const { enabledFeatures, toggleFeature, isLoading } = useFeatures();

  useEffect(() => {
    // Clear feature overrides only on a CONFIRMED signed-out state. `session`
    // is null on every mount — always for local users, and transiently for
    // signed-in users while Firebase auth loads — so gating on `!session`
    // alone wiped every user's feature prefs + USER_PLAN on each app open
    // (e.g. silently re-enabling telemetry a user had disabled). Waiting for
    // auth to finish loading avoids clearing during that transient window.
    if (!session && !authLoading) {
      void (async () => {
        try {
          const features = Object.keys(FEATURE_FLAGS) as FeatureName[];
          await Promise.all([
            ...features.map(feature => storage.remove(createFeatureKey(feature))),
            storage.remove(STORAGE_KEYS.USER_PLAN),
          ]);
        } catch (error) {
          console.error('Error clearing features:', error);
        }
      })();
    }
  }, [session, authLoading]);

  return (
    <FeatureErrorBoundary>
      <FeaturesContext.Provider value={{ enabledFeatures, toggleFeature, isLoading }}>
        {isLoading ? <LoadingSpinner /> : children}
      </FeaturesContext.Provider>
    </FeatureErrorBoundary>
  );
}
